import React, { useContext, useMemo } from "react";
import { InsightCard } from "./InsightCard";
import NoInsights from "./components/noInsights/NoInsights";
import { useInsightState } from "./context";
import InsightDetails from "./InsightDetails";
import { InsightContext } from "./context/InsightContextProvider";
import { setSelectedOpportunityId } from "./context/insightSlice";
import { Stack } from "@altimateai/lego";
import InsightSectionHeader from "./components/InsightsSectionHeader";
import { GetOpportunitiesDataResponse } from "@/pages/explorer/types";

interface InsightSectionNewProps {
  setOpportunityId: (item: string) => void;
  onAskAI?: (e: React.MouseEvent, item: GetOpportunitiesDataResponse) => void;
  emptyTitle?: string;
  emptySubTitle?: string;
  showTimeSavings?: boolean;
  onDateRangeChange?: (startDate: Date, endDate: Date) => void;
}

export const InsightSectionNew: React.FC<InsightSectionNewProps> = ({
  setOpportunityId,
  onAskAI,
  emptyTitle,
  emptySubTitle,
  showTimeSavings = false,
  onDateRangeChange,
}) => {
  const {
    opportunities: opportunitiesFromContext = [],
    showMultiStatementError,
    selectedOpportunityId,
  } = useInsightState();
  const { dispatch } = useContext(InsightContext);

  // Sorting and filtering is handled by the parent page components
  // (QueryDetails, GroupDetails) before passing opportunities via context.
  const opportunities = useMemo(() => {
    const opportunityLinenumberMap: Record<string, number> = {};
    const opportunityCountMap: Record<string, number> = {};

    // Count occurrences of each opportunity_name
    opportunitiesFromContext.forEach(o => {
      opportunityCountMap[o.opportunity_name] = (opportunityCountMap[o.opportunity_name] || 0) + 1;
    });

    return opportunitiesFromContext.map(o => {
      if (o.linenumber) {
        return o;
      }
      if (opportunityCountMap[o.opportunity_name] === 1) {
        // Do not add linenumber if only one item exists
        return o;
      }
      opportunityLinenumberMap[o.opportunity_name] =
        opportunityLinenumberMap[o.opportunity_name] || 0;
      opportunityLinenumberMap[o.opportunity_name] += 1;
      return {
        ...o,
        linenumber: String(opportunityLinenumberMap[o.opportunity_name]),
      };
    });
  }, [opportunitiesFromContext]);

  const selectedOpportunity = useMemo(
    () => opportunities.find(o => o.opportunity_id === selectedOpportunityId),
    [opportunities, selectedOpportunityId]
  );

  if (showMultiStatementError) {
    return (
      <>
        <InsightSectionHeader onDateRangeChange={onDateRangeChange} />
        <NoInsights
          title={emptyTitle || "No opportunities detected!"}
          subTitle="Opportunities for multi-statement SQL queries are not currently supported. We're actively working on this support."
        />
      </>
    );
  }

  if (!opportunities || opportunities.length === 0) {
    return (
      <>
        <InsightSectionHeader onDateRangeChange={onDateRangeChange} />

        <NoInsights
          title={emptyTitle || "No opportunities detected!"}
          subTitle={emptySubTitle || "No opportunities found for this resource."}
        />
      </>
    );
  }

  if (selectedOpportunity) {
    return (
      <div className="al-w-full" aria-label="Opportunity details">
        <InsightDetails
          opportunity={selectedOpportunity}
          savingsInfo={selectedOpportunity.savings_attribution}
          onBack={() => {
            dispatch(setSelectedOpportunityId(null));
            setOpportunityId("");
          }}
        />
      </div>
    );
  }

  return (
    <div className="al-py-4" aria-label="Opportunities">
      <InsightSectionHeader onDateRangeChange={onDateRangeChange} />
      <div className="al-relative al-overflow-hidden">
        {/* Grid View */}
        <div className="al-grid al-grid-cols-1 lg:al-grid-cols-2 al-gap-6">
          {opportunities.map(opportunity => {
            return (
              <Stack key={opportunity.opportunity_id}>
                <InsightCard
                  opportunity={opportunity}
                  setOpportunityId={setOpportunityId}
                  onAskAI={onAskAI}
                  showTimeSavings={showTimeSavings}
                />
              </Stack>
            );
          })}
        </div>
      </div>
    </div>
  );
};
